import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Target, RefreshCw, Calendar, Search, ArrowUpDown, ArrowRight, AlertCircle,
} from "lucide-react";
import { AdsSyncBadge } from "@/components/agency/AdsSyncBadge";

/**
 * META FASE 5 — Agency "Tracking Ads": panoramica Ads cross-tenant che
 * sostituisce il foglio Excel. Solo agency_admin. Currency per tenant (nessuna
 * somma cieca tra valute diverse: totali raggruppati per currency e KPI
 * derivati RICALCOLATI dai totali). Stessa semantica KPI di META 4.
 */
export const fmtCur = (n, cur = "EUR") =>
  n == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: cur || "EUR", maximumFractionDigits: 0 }).format(n);
export const fmtNum = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
export const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
export const fmtRoas = (n) => (n == null ? "—" : `${Number(n).toFixed(2)}×`);

const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const STATUS = {
  ok: null,
  no_data_in_period: { label: "Nessun dato nel periodo", tone: "amber" },
  ad_account_not_selected: { label: "Account Ads non selezionato", tone: "slate" },
  meta_not_connected: { label: "Meta non collegato", tone: "slate" },
};

const COLS = [
  { key: "spend", label: "Spesa", cur: true, num: true },
  { key: "leads", label: "Lead", num: true },
  { key: "cpl", label: "CPL", cur: true, num: true },
  { key: "appointments_set", label: "Appuntamenti", num: true },
  { key: "setting_rate", label: "Setting", pct: true, num: true },
  { key: "cpa", label: "CPA", cur: true, num: true },
  { key: "showed_up", label: "Presentati", num: true },
  { key: "show_up_rate", label: "Show Up", pct: true, num: true },
  { key: "new_customers", label: "Nuovi clienti", num: true },
  { key: "cac", label: "CAC", cur: true, num: true },
  { key: "front_end_revenue", label: "Fatturato FE", cur: true, num: true },
  { key: "back_end_revenue", label: "Fatturato BE", cur: true, num: true },
  { key: "total_revenue", label: "Fatturato Tot", cur: true, num: true },
  { key: "front_end_roas", label: "ROAS FE", roas: true, num: true },
  { key: "total_roas", label: "ROAS Tot", roas: true, num: true },
  { key: "lead_to_customer_rate", label: "Lead→Cliente", pct: true, num: true },
];

function cellValue(row, col) {
  const v = row[col.key];
  if (v == null) return "—";
  if (col.cur) return fmtCur(v, row.currency);
  if (col.pct) return fmtPct(v);
  if (col.roas) return fmtRoas(v);
  return fmtNum(v);
}

export default function AgencyAdsPerformance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [since, setSince] = useState(daysAgo(30));
  const [until, setUntil] = useState(daysAgo(0));
  const [q, setQ] = useState("");
  const [connected, setConnected] = useState("all"); // all | yes | no
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: "spend", dir: "desc" });
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const myId = ++reqRef.current;
    setLoading(true); setError(null);
    try {
      let url = `/agency/ads/overview?start_date=${since}&end_date=${until}`;
      if (q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      if (connected !== "all") url += `&connected=${connected === "yes"}`;
      const r = await api.get(url);
      if (reqRef.current !== myId) return; // ignora risposta obsoleta
      setData(r.data);
    } catch (e) {
      if (reqRef.current !== myId) return;
      setError(e?.response?.status === 403 ? "Accesso riservato agli amministratori agenzia." : "Errore nel caricamento dei dati.");
      setData(null);
    }
    if (reqRef.current === myId) setLoading(false);
  }, [since, until, q, connected]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    const { key, dir } = sort;
    list.sort((a, b) => {
      const av = a[key] == null ? -Infinity : a[key];
      const bv = b[key] == null ? -Infinity : b[key];
      if (key === "tenant_name") return dir === "asc" ? String(a.tenant_name).localeCompare(b.tenant_name) : String(b.tenant_name).localeCompare(a.tenant_name);
      return dir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [data, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  if (user && user.role !== "agency_admin") {
    return <div data-testid="ads-perf-denied" className="sp-card p-8 text-center text-sm text-[color:var(--sp-text-muted)]">Sezione riservata agli amministratori agenzia.</div>;
  }

  return (
    <div data-testid="agency-ads-performance" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[color:var(--sp-brand)]/10 grid place-items-center">
            <Target className="h-5 w-5 text-[color:var(--sp-brand)]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black">Tracking Ads</h1>
            <p className="text-sm text-[color:var(--sp-text-muted)]">Performance Ads di tutti i saloni · attività del periodo</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => { setSince(daysAgo(d)); setUntil(daysAgo(0)); }}
              data-testid={`ads-perf-preset-${d}`}
              className="text-xs font-bold px-2.5 py-1.5 rounded-lg border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]">{d}gg</button>
          ))}
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="ads-perf-since" className="sp-input !py-1 !text-xs" />
            <span>→</span>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="ads-perf-until" className="sp-input !py-1 !text-xs" />
          </div>
          <button onClick={load} data-testid="ads-perf-refresh" className="p-2 rounded-lg border border-[color:var(--sp-border)]">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca salone…"
            data-testid="ads-perf-search" className="sp-input !py-1.5 !pl-8 !text-sm w-56" />
        </div>
        <div className="flex items-center gap-1 text-xs">
          {[["all", "Tutti"], ["yes", "Meta collegato"], ["no", "Non collegato"]].map(([v, l]) => (
            <button key={v} onClick={() => setConnected(v)} data-testid={`ads-perf-filter-${v}`}
              className={`px-2.5 py-1.5 rounded-lg border font-bold ${connected === v ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-[color:var(--sp-border)]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {error && <div data-testid="ads-perf-error" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {error}</div>}
      {loading && !data && <div className="text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>}
      {!loading && data && rows.length === 0 && !error && (
        <div data-testid="ads-perf-empty" className="sp-card p-10 text-center text-sm text-[color:var(--sp-text-muted)]">Nessun salone corrisponde ai filtri selezionati.</div>
      )}

      {/* Tabella cross-tenant */}
      {data && rows.length > 0 && (
        <div className="rounded-2xl border border-[color:var(--sp-border)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="ads-perf-table">
              <thead>
                <tr className="bg-[color:var(--sp-subtle)]">
                  <th onClick={() => toggleSort("tenant_name")}
                    className="sticky left-0 z-20 bg-[color:var(--sp-subtle)] text-left px-4 py-3 font-black text-xs uppercase tracking-wider cursor-pointer min-w-[180px] border-r border-[color:var(--sp-border)]">
                    <span className="inline-flex items-center gap-1">Salone <ArrowUpDown className="h-3 w-3" /></span>
                  </th>
                  {COLS.map((c) => (
                    <th key={c.key} onClick={() => toggleSort(c.key)}
                      data-testid={`ads-perf-th-${c.key}`}
                      className="px-3 py-3 font-black text-[11px] uppercase tracking-wider cursor-pointer text-right whitespace-nowrap hover:text-[color:var(--sp-brand)]">
                      <span className="inline-flex items-center gap-1">{c.label}<ArrowUpDown className="h-3 w-3 opacity-50" /></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = STATUS[r.status];
                  return (
                    <tr key={r.tenant_id} onClick={() => navigate(`/admin/ads-performance/${r.tenant_id}`)}
                      data-testid={`ads-perf-row-${r.tenant_id}`}
                      className="border-t border-[color:var(--sp-border)] hover:bg-[color:var(--sp-brand)]/5 cursor-pointer">
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 font-bold border-r border-[color:var(--sp-border)] group-hover:bg-transparent">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[150px]">{r.tenant_name}</span>
                          <ArrowRight className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
                        </div>
                        {st && <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${st.tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{st.label}</span>}
                        <div className="mt-1"><AdsSyncBadge sync={r.sync} testid={`ads-sync-${r.tenant_id}`} /></div>
                      </td>
                      {COLS.map((c) => (
                        <td key={c.key} className={`px-3 py-3 text-right whitespace-nowrap tabular-nums ${c.key === "total_revenue" || c.key === "total_roas" ? "font-bold" : ""}`}>
                          {r.status === "ok" || r.status === "no_data_in_period" ? cellValue(r, c) : "—"}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="text-[11px] text-[color:var(--sp-text-muted)]">I ratio operativi (Setting / Show Up) possono superare il 100%: un appuntamento può essere svolto nel periodo pur essendo stato fissato prima. Le valute non vengono sommate tra loro.</p>
    </div>
  );
}
